import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { ProductOrServiceRestDataProvider } from '../../../data/ProductOrServiceRestDataProvider';
import { VatRateRestDataProvider } from '../../../data/VatRateRestDataProvider';
import { SupplierRestDataProvider } from '../../../data/SupplierRestDataProvider';
import { ReferenceNumberConfigRestDataProvider } from '../../../data/ReferenceNumberConfigRestDataProvider';
import { ProductOrService } from '../../../models/ProductOrService';
import { VatRate } from '../../../models/VatRate';
import { Supplier } from '../../../models/Supplier';
import ProductOrServiceDialog from './ProductOrServiceDialog';
import './ProductsAndServices.scss';
import '../../../styles/GenericSiteStyles.scss';
import { useTranslation } from 'react-i18next';

const ProductsAndServices: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [products, setProducts] = useState<ProductOrService[]>([]);
  const [vatRates, setVatRates] = useState<VatRate[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductOrService | null>(null);

  const provider = useMemo(() => new ProductOrServiceRestDataProvider(), []);
  const vatProvider = useMemo(() => new VatRateRestDataProvider(), []);
  const supplierProvider = useMemo(() => new SupplierRestDataProvider(), []);
  const refNumProvider = useMemo(() => new ReferenceNumberConfigRestDataProvider(), []);

  const [nextArticleCode, setNextArticleCode] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [prods, vats, sups] = await Promise.all([
        provider.getAll(),
        vatProvider.getAll(),
        supplierProvider.getAll(),
      ]);
      setProducts(prods);
      setVatRates(vats);
      setSuppliers(sups);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider, vatProvider, supplierProvider]);

  useEffect(() => { loadData(); }, [loadData]);

  const peekArticleCode = useCallback(async () => {
    try {
      const { formattedNumber } = await refNumProvider.peek('PS');
      setNextArticleCode(formattedNumber);
    } catch {
      setNextArticleCode('');
    }
  }, [refNumProvider]);

  useEffect(() => { peekArticleCode(); }, [peekArticleCode]);

  const columns: DetailedListColumn<ProductOrService>[] = useMemo(() => [
    {
      key: 'articleCode',
      header: t('screens:productsAndServices.columns.code'),
      dataField: 'articleCode',
      type: 'string',
      width: 100,
      fixedWidth: true,
    },
    {
      key: 'name',
      header: t('screens:productsAndServices.columns.name'),
      dataField: 'name',
      type: 'string',
    },
    {
      key: 'category',
      header: t('screens:productsAndServices.columns.category'),
      dataField: 'category',
      type: 'string',
      width: 130,
      fixedWidth: true,
    },
    {
      key: 'unit',
      header: t('screens:productsAndServices.columns.unit'),
      dataField: 'unit',
      type: 'string',
      width: 70,
      fixedWidth: true,
    },
    {
      key: 'unitPriceExVat',
      header: t('screens:productsAndServices.columns.salesPrice'),
      dataField: 'unitPriceExVat',
      width: 120,
      fixedWidth: true,
      render: p => `€ ${Number(p.unitPriceExVat).toFixed(2)}`,
    },
    {
      key: 'vat',
      header: t('screens:productsAndServices.columns.vat'),
      dataField: 'vat',
      width: 130,
      fixedWidth: true,
      render: p => p.vat ? `${p.vat.name} (${p.vat.percentage}%)` : <span className="text-muted">—</span>,
    },
    {
      key: 'isActive',
      header: t('screens:productsAndServices.columns.status'),
      dataField: 'isActive',
      width: 95,
      fixedWidth: true,
      render: p => p.isActive
        ? <span className="badge bg-success">{t('common:active')}</span>
        : <span className="badge bg-secondary">{t('common:inactive')}</span>,
    },
  ], [t]);

  const openEdit = (product: ProductOrService) => {
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const openAdd = () => {
    setSelectedProduct({
      id: '',
      articleCode: nextArticleCode,
      name: '',
      category: '',
      description: '',
      isActive: true,
      unit: '',
      unitPriceExVat: 0,
      purchasePriceExVat: 0,
      vat: undefined,
      revenueAccountCode: '',
      costAccountCode: '',
      preferredSupplierId: undefined,
      supplierArticleCode: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSave = async (product: ProductOrService) => {
    try {
      if (!product.id) {
        const articleCode = await refNumProvider.commit('PS');
        await provider.add({ ...product, id: crypto.randomUUID(), articleCode });
        peekArticleCode();
      } else {
        await provider.update(product.id, product);
      }
      closeDialog();
      await loadData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (product: ProductOrService) => {
    try {
      await provider.delete(product.id);
      closeDialog();
      await loadData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
      <div className="pos-page">
        <MainScreenHeader title={t('screens:productsAndServices.title')} />
        {error && <ErrorMessage message={error} />}
        <DetailedListComponent
          dataSource={{ data: products, setData: setProducts }}
          columns={columns}
          rowKey={p => p.id}
          editRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          onEditRequest={openEdit}
          onAddRequest={openAdd}
        />
        {loading && <div className="pos-loading">{t('common:loading')}</div>}
        <ProductOrServiceDialog
          open={dialogOpen}
          product={selectedProduct}
          vatRates={vatRates}
          suppliers={suppliers}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={closeDialog}
        />
      </div>
    </RoleGuard>
  );
};

export default ProductsAndServices;
